import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
  type PageTheme,
} from '@backstage/theme';

declare module '@backstage/theme' {
  interface OverrideComponentNameToClassKeys {
    BackstageSidebarPage: 'root' | 'content';
  }
}

const lightPalette = palettes.light;
const darkPalette = palettes.dark;

/* Without this, `createUnifiedTheme` falls back to @backstage/theme's
 * DEFAULT_FONT_FAMILY ('"Helvetica Neue", Helvetica, Roboto, Arial') and writes it
 * onto every MUI Typography — so the page header rendered in Helvetica Neue while
 * the Tailwind half rendered in the body font. Resolves to the same
 * `--font-sans` token as portal.css and tailwind.config.js. */
const fontFamily = 'var(--font-sans)';

/* Backstage picks a page header gradient per `themeId`; the stock set is purple
 * and off-brand, so every id resolves to the same Vivicta green instead. */
const PAGE_THEME_IDS = [
  'home',
  'app',
  'apis',
  'card',
  'documentation',
  'library',
  'other',
  'service',
  'tool',
  'website',
];

const brandPageThemes = (colors: string[]): Record<string, PageTheme> =>
  Object.fromEntries(
    PAGE_THEME_IDS.map(id => [
      id,
      genPageTheme({ colors, shape: shapes.wave }),
    ]),
  );

/* `SidebarPage` reserves room for Backstage's own drawer by padding its root
 * left by the drawer width. VivictaSidebar replaces that drawer and is fixed at
 * 16rem from `lg` up, off-canvas below it, so the reservation has to be
 * restated here — otherwise the routed content sits underneath the sidebar.
 * The width is `--vivicta-sidebar-width` (portal.css), which drops to 0 when
 * the user collapses the sidebar, so the content reclaims the space.
 * `PortalTopBar` (T2.1) is `position: fixed` the same way, at every
 * breakpoint, so its `h-14` (3.5rem) is reserved as `padding-top`
 * unconditionally rather than behind the `lg` media query.
 *
 * This has to go through the theme rather than a stylesheet: `SidebarPage`'s
 * classes are JSS, and MUI v4 only emits the readable `BackstageSidebarPage-*`
 * names in development. A production bundle gets `jss1`, `jss2`, … so a
 * `[class*='BackstageSidebarPage-root']` selector silently matches nothing.
 * `!important` because Backstage's own padding is a function of props, which
 * MUI puts in a separate, later-injected dynamic stylesheet. */
const sidebarPageOverride = {
  BackstageSidebarPage: {
    styleOverrides: {
      root: {
        minHeight: '100vh',
        paddingLeft: '0 !important',
        paddingTop: '3.5rem !important',
        paddingBottom: '0 !important',
        // `screen` so it stays out of the way of SidebarPage's own print reset,
        // which JSS emits ahead of this rule and so cannot override it.
        '@media screen and (min-width: 1024px)': {
          paddingLeft: 'var(--vivicta-sidebar-width) !important',
        },
      },
    },
  },
} as const;

/* No page banner (see `Header` in packages/ui page.tsx): the top bar names the
 * page. Stock Backstage pages keep their header for screen readers only. Theme
 * rather than stylesheet for the same JSS reason as above. */
const headerOverride = {
  BackstageHeader: {
    styleOverrides: {
      header: {
        position: 'absolute',
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
      },
    },
  },
} as const;

export const portalTheme = createUnifiedTheme({
  fontFamily,
  palette: {
    ...lightPalette,
    primary: {
      ...lightPalette.primary,
      main: '#173c33',
      dark: '#102d26',
      light: '#e8f0ea',
      contrastText: '#fbfaf5',
    },
    secondary: {
      main: '#49665b',
      dark: '#28443a',
      light: '#dde8e0',
      contrastText: '#fbfaf5',
    },
    background: {
      ...lightPalette.background,
      default: '#f4f2ea',
      paper: '#fbfaf5',
    },
    text: {
      primary: '#16392f',
      // Mirrors --muted-foreground in packages/ui/src/globals.css. Kept in step
      // by hand until ADR-0011's two-palette split is resolved; the previous
      // ramp (#5f746b / #81938a) failed WCAG AA on background.default.
      secondary: '#576b63',
      hint: '#5e736b',
    },
    navigation: {
      ...lightPalette.navigation,
      background: '#e6ebe2',
      indicator: '#173c33',
      color: '#4f675d',
      selectedColor: '#16392f',
      navItem: {
        ...lightPalette.navigation.navItem,
        hoverBackground: '#d7e0d5',
      },
      submenu: {
        ...lightPalette.navigation.submenu,
        background: '#edf1eb',
      },
    },
    pinSidebarButton: {
      ...lightPalette.pinSidebarButton,
      icon: '#16392f',
      background: '#d7e0d5',
    },
    tabbar: {
      ...lightPalette.tabbar,
      indicator: '#173c33',
    },
    border: '#d6ddd4',
    textSubtle: '#5e736b',
    textVerySubtle: '#60716b',
  },
  defaultPageTheme: 'home',
  pageTheme: brandPageThemes(['#173c33', '#2f6b56']),
  components: {
    ...sidebarPageOverride,
    ...headerOverride,
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#e6ebe2',
          color: '#16392f',
          borderRight: '1px solid rgba(22, 57, 47, 0.08)',
        },
      },
    },
  },
});

/**
 * Dark variant of the portal theme. Colours are matched to the `.dark` HSL
 * design tokens in packages/ui/src/globals.css so MUI chrome and
 * Tailwind/shadcn content stay visually consistent when the app is in dark
 * mode.
 */
export const portalDarkTheme = createUnifiedTheme({
  fontFamily,
  palette: {
    ...darkPalette,
    primary: {
      ...darkPalette.primary,
      main: '#59c090',
      dark: '#30413a',
      light: '#273531',
      contrastText: '#0e1b17',
    },
    secondary: {
      main: '#30413a',
      dark: '#232f2c',
      light: '#273531',
      contrastText: '#eaeee8',
    },
    background: {
      ...darkPalette.background,
      default: '#0e1b17',
      paper: '#152320',
    },
    text: {
      primary: '#eaeee8',
      secondary: '#a3ad9f',
      hint: '#a3ad9f',
    },
    navigation: {
      ...darkPalette.navigation,
      background: '#152320',
      indicator: '#59c090',
      color: '#a3ad9f',
      selectedColor: '#eaeee8',
      navItem: {
        ...darkPalette.navigation.navItem,
        hoverBackground: '#232f2c',
      },
      submenu: {
        ...darkPalette.navigation.submenu,
        background: '#0e1b17',
      },
    },
    pinSidebarButton: {
      ...darkPalette.pinSidebarButton,
      icon: '#eaeee8',
      background: '#232f2c',
    },
    tabbar: {
      ...darkPalette.tabbar,
      indicator: '#59c090',
    },
    border: '#2b3b37',
    textSubtle: '#a3ad9f',
    textVerySubtle: '#788c85',
  },
  defaultPageTheme: 'home',
  pageTheme: brandPageThemes(['#122a23', '#1f5343']),
  components: {
    ...sidebarPageOverride,
    ...headerOverride,
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#152320',
          color: '#eaeee8',
          borderRight: '1px solid rgba(234, 238, 232, 0.08)',
        },
      },
    },
  },
});
